import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useIdleTimeout
 *
 * Tracks real user activity (pointer, keyboard, touch, wheel, scroll) and
 * fires `onTimeout` after `timeoutMs` of inactivity, surfacing a warning
 * state for the final `warningMs` so the UI can offer a "stay signed in"
 * escape hatch.
 *
 * In-flight `fetch` requests (long uploads, report saves) defer the actual
 * timeout: the callback only fires once no requests are outstanding, so a
 * user is never signed out mid-upload. Passive activity while the warning is
 * visible does NOT dismiss it — the user must explicitly choose to stay.
 */

const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const;

const CHECK_INTERVAL_MS = 1000;
const ACTIVITY_THROTTLE_MS = 1000;

export interface UseIdleTimeoutOptions {
  enabled: boolean;
  timeoutMs: number;
  warningMs: number;
  onTimeout: () => void;
}

export interface UseIdleTimeoutResult {
  isWarning: boolean;
  remainingSeconds: number;
  staySignedIn: () => void;
}

export function useIdleTimeout({
  enabled,
  timeoutMs,
  warningMs,
  onTimeout,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(() => Math.ceil(warningMs / 1000));
  const lastActivityRef = useRef(Date.now());
  const isWarningRef = useRef(false);
  const firedRef = useRef(false);
  const inFlightRequestsRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const staySignedIn = useCallback(() => {
    lastActivityRef.current = Date.now();
    isWarningRef.current = false;
    setIsWarning(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    firedRef.current = false;
    isWarningRef.current = false;
    lastActivityRef.current = Date.now();

    const recordActivity = () => {
      if (isWarningRef.current || firedRef.current) return;
      const now = Date.now();
      if (now - lastActivityRef.current >= ACTIVITY_THROTTLE_MS) {
        lastActivityRef.current = now;
      }
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, recordActivity, { capture: true, passive: true });
    }

    const originalFetch = window.fetch;
    const wrappedFetch: typeof window.fetch = (...args) => {
      inFlightRequestsRef.current += 1;
      const settle = () => {
        inFlightRequestsRef.current = Math.max(0, inFlightRequestsRef.current - 1);
      };
      const promise = originalFetch(...args);
      promise.then(settle, settle);
      return promise;
    };
    window.fetch = wrappedFetch;

    const intervalId = window.setInterval(() => {
      if (firedRef.current) return;

      const idleMs = Date.now() - lastActivityRef.current;
      const remainingMs = timeoutMs - idleMs;

      if (remainingMs <= 0) {
        if (inFlightRequestsRef.current > 0) return;
        firedRef.current = true;
        isWarningRef.current = false;
        setIsWarning(false);
        onTimeoutRef.current();
        return;
      }

      if (remainingMs <= warningMs) {
        isWarningRef.current = true;
        setIsWarning(true);
        setRemainingSeconds(Math.max(0, Math.ceil(remainingMs / 1000)));
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, recordActivity, { capture: true });
      }
      window.clearInterval(intervalId);
      if (window.fetch === wrappedFetch) {
        window.fetch = originalFetch;
      }
      isWarningRef.current = false;
      setIsWarning(false);
    };
  }, [enabled, timeoutMs, warningMs]);

  return { isWarning, remainingSeconds, staySignedIn };
}
